// A labelled traceroute: the AS path, where the trace aimed and how it
// ended, and the hops grouped by AS (docs/adr/2026-10-04-as-path-monitoring.md
// §1–§2). Ad-hoc traceroute results and PATH monitors share it.
import { Fragment } from "react";
import { ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { asHandle, groupHops, targetNetwork, traceOutcome, type PathAS, type TraceHop, type TraceStop, type TraceTarget } from "@/lib/aspath";
import { dateTime, ms, pct } from "@/lib/format";

/** A stored trace, as the master labels it (asn.Trace). */
export type Trace = { hops: TraceHop[]; stop?: TraceStop; target?: TraceTarget; as_path?: PathAS[] | null; asn_dataset?: string };

/** An AS path as a line of AS chips. */
export function ASPathLine({ path, label = "AS path" }: { path: PathAS[]; label?: string }) {
  if (path.length === 0) return <p className="text-sm text-muted-foreground">No hop answered from a public address, so there is no AS path.</p>;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {path.map((a, i) => (
        <span key={`${a.asn}-${i}`} className="contents">
          {i > 0 && <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />}
          <Badge variant="outline" title={[`AS${a.asn}`, a.name, a.country].filter(Boolean).join(" · ")}>
            AS{a.asn} <span className="font-normal text-muted-foreground">{asHandle(a.name)}</span>
          </Badge>
        </span>
      ))}
    </div>
  );
}

/** Where the trace aimed and how it ended (probes from 0.1.1-dev.7 report both). */
function TraceTargetLine({ stop, target, hops }: { stop?: TraceStop; target?: TraceTarget; hops: TraceHop[] }) {
  const outcome = traceOutcome(stop, target, hops, (v) => ms(v));
  if (!target && !outcome) return null;
  const network = target ? targetNetwork(target) : "";
  return (
    <p className="text-sm">
      {target && (
        <>
          <span className="text-muted-foreground">Target </span>
          <span className="font-mono text-xs">{target.address}</span>
          {network && <span className="text-muted-foreground"> · {network}</span>}
          {outcome && ". "}
        </>
      )}
      {outcome}
    </p>
  );
}

/** The whole trace. pathLine=false leaves the AS path out (the caller shows it). */
export function TraceView({ trace, pathLine = true }: { trace: Trace; pathLine?: boolean }) {
  const labelled = Array.isArray(trace.as_path);
  return (
    <div className="grid gap-2">
      {labelled && pathLine && <ASPathLine path={trace.as_path as PathAS[]} />}
      <TraceTargetLine stop={trace.stop} target={trace.target} hops={trace.hops} />
      <div className="max-h-80 overflow-auto rounded-md border">
        <table className="tabular w-full text-sm">
          <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
            <tr><th className="px-3 py-1.5 text-right">Hop</th><th className="px-3 py-1.5 text-left">Address</th><th className="px-3 py-1.5 text-right">Avg RTT</th><th className="px-3 py-1.5 text-right">Loss</th></tr>
          </thead>
          <tbody>
            {groupHops(trace.hops).map((g) => (
              <Fragment key={`${g.key}-${g.hops[0].ttl}`}>
                {g.key && (
                  <tr className="border-t bg-muted/40">
                    <td colSpan={4} className="truncate px-3 py-1 text-xs font-medium" title={g.title}>{g.label}</td>
                  </tr>
                )}
                {g.hops.map((h) => (
                  <tr key={h.ttl} className="border-t">
                    <td className="px-3 py-1 text-right">{h.ttl}</td>
                    <td className="px-3 py-1 font-mono text-xs">{h.address || "*"}</td>
                    <td className="px-3 py-1 text-right">{h.address ? ms(h.rtt_avg_ms) : "—"}</td>
                    <td className={"px-3 py-1 text-right " + (h.loss_pct >= 100 ? "text-status-down" : h.loss_pct > 0 ? "text-status-degraded" : "")}>{pct(h.loss_pct)}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {labelled && (
        <p className="text-xs text-muted-foreground">
          Inferred from the traceroute, so it can be wrong where networks meet. AS data from iptoasn.com, version {dateTime(trace.asn_dataset)}.
        </p>
      )}
    </div>
  );
}
